import { type Query, type QueryClient } from '@tanstack/react-query';

const DETAIL_QUERY_ID = 'cardsGetDispute';
const LIST_QUERY_ID = 'cardsListCardDisputes';

interface DisputeQueryKeyHead {
  _id?: string;
  path?: { card_dispute_id?: string };
  query?: { card_transaction_id?: string };
}

export interface InvalidateCardDisputeQueriesOptions {
  disputeId?: string;
  transactionId?: string;
}

const headOf = (query: Query): DisputeQueryKeyHead | undefined => query.queryKey[0] as DisputeQueryKeyHead | undefined;

const matchesDetail = (head: DisputeQueryKeyHead, disputeId?: string): boolean =>
  Boolean(disputeId) && head._id === DETAIL_QUERY_ID && head.path?.card_dispute_id === disputeId;

// A mutation can affect unfiltered lists and lists filtered to the dispute's transaction; other
// filters cannot gain rows, so leave them alone.
const matchesList = (head: DisputeQueryKeyHead, transactionId?: string): boolean => {
  if (head._id !== LIST_QUERY_ID) {
    return false;
  }
  if (!transactionId) {
    return true;
  }
  const filter = head.query?.card_transaction_id;
  return filter === undefined || filter === transactionId;
};

/**
 * Refresh the dispute detail and the affected dispute lists after a mutation.
 *
 * The configured query client appends the platform/live-mode context to every cache key, and the
 * session store clears the cache on platform switches, so matching here cannot corrupt a view
 * living in another platform/mode context.
 */
export const invalidateCardDisputeQueries = (
  queryClient: QueryClient,
  { disputeId, transactionId }: InvalidateCardDisputeQueriesOptions = {}
) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const head = headOf(query);
      if (!head) {
        return false;
      }
      return matchesDetail(head, disputeId) || matchesList(head, transactionId);
    },
  });
};
